import { useMutation, useQuery } from '@tanstack/react-query';
import { Avatar, Button, cx, Field, Icon, Input, Notice, RaroBrand, Serif, VoiceWave } from '@raro/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { RealtimeVoice } from '../components/RealtimeVoice';
import { useWakeLock } from '../lib/wake-lock';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000';

interface Peek {
  id: string;
  kind: 'briefing' | 'interview';
  title: string;
  status: 'open' | 'in_progress' | 'done' | 'expired';
  organization: string;
  project: string | null;
  participant: { name: string | null; role: string | null; relation: string | null };
  maxTurns: number;
  turns: { id: string; index: number; role: 'user' | 'assistant'; text: string; attachments: { fileId: string; name: string; mimeType: string }[] }[];
  serverTranscribes: boolean;
  serverSpeaks: boolean;
  realtimeAvailable: boolean;
  /** o abre escrito para esta conversa: sobre o quê é, para quem, por onde passa */
  referredBy?: string | null;
  intro?: { headline: string; body: string; topics: string[]; minutes: number } | null;
  invitedBy?: string | null;
}

/** Formato de gravação que este navegador sabe produzir (Chrome e Android: webm; Safari e iPhone: mp4). */
const recordingMime = () => (typeof MediaRecorder === 'undefined' ? null : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((m) => MediaRecorder.isTypeSupported(m)) ?? '');
const MAX_RECORDING_S = 300;
type Recording = { recorder: MediaRecorder; stream: MediaStream; chunks: Blob[]; timer: number; startedAt: number; send: boolean; ctx?: AudioContext; analyser?: AnalyserNode };

async function pub<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API_URL}${path}`, init);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((body as { message?: string }).message ?? `erro ${r.status}`);
  return body as T;
}

/**
 * Conversa pública com a Machine (sem login, por link). A pessoa fala ou escreve; a Machine pergunta e fala de volta.
 * Voz pelo navegador (Web Speech): reconhecimento em pt-BR e síntese. Foto e vídeo entram no meio da conversa.
 * No fim, tudo vira fonte na Machine.
 */
export function ConversaPage() {
  const { token = '' } = useParams();
  const q = useQuery({ queryKey: ['conversa', token], queryFn: () => pub<Peek>(`/conversa/${token}`), retry: false });
  const [turns, setTurns] = useState<Peek['turns']>([]);
  const [intro, setIntro] = useState({ name: '', role: '', relation: '' });
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  // gravando a fala, a tela fica ligada
  useWakeLock(listening);
  const [seconds, setSeconds] = useState(0);
  const [micError, setMicError] = useState<string | null>(null);
  const [speaking, setSpeaking] = useState(false);
  const [voice, setVoice] = useState(true);
  const [files, setFiles] = useState<File[]>([]);
  const [done, setDone] = useState(false);
  const [finished, setFinished] = useState(false);
  const [mode, setMode] = useState<'turns' | 'live'>('turns');
  const recRef = useRef<Recording | null>(null);
  const levels = useRef<Uint8Array<ArrayBuffer> | null>(null);
  /** O espectro do microfone enquanto grava, para a onda. */
  const micLevels = useCallback(() => { const an = recRef.current?.analyser; if (!an) return null; if (!levels.current || levels.current.length !== an.frequencyBinCount) levels.current = new Uint8Array(an.frequencyBinCount); an.getByteFrequencyData(levels.current); return levels.current; }, []);
  const lastSent = useRef<{ text: string; files: File[]; audio?: File } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const canListen = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia) && recordingMime() !== null;
  const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;

  useEffect(() => { if (q.data) { setTurns(q.data.turns); setDone(q.data.status === 'done'); setFinished(q.data.status === 'done'); setIntro({ name: q.data.participant.name ?? '', role: q.data.participant.role ?? '', relation: q.data.participant.relation ?? '' }); } }, [q.data]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [turns.length, listening]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  /** Chamado dentro de um toque da pessoa (começar, gravar, enviar): destrava o áudio para as próximas perguntas da Machine. */
  const unlockAudio = useCallback(() => {
    if (audioRef.current) return;
    const a = new Audio();
    a.setAttribute('playsinline', '');
    a.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';
    audioRef.current = a;
    void a.play().catch(() => undefined);
  }, []);
  const speak = useCallback((t: string): Promise<void> => {
    if (!voice) return Promise.resolve();
    if (q.data?.serverSpeaks) {
      // voz da Machine pelo servidor (OpenAI TTS); cai no navegador se falhar. resolve quando a fala termina.
      return (async () => {
        try {
          const r = await fetch(`${API_URL}/conversa/${token}/speak`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text: t }) });
          if (r.status === 204 || !r.ok) throw new Error('sem voz');
          const url = URL.createObjectURL(await r.blob());
          // sempre o MESMO elemento, já destravado por um toque: criar um novo a cada pergunta fazia o iPhone calar depois das boas-vindas
          const a = audioRef.current ?? new Audio();
          audioRef.current = a;
          a.pause();
          a.src = url;
          setSpeaking(true);
          const ended = new Promise<void>((res) => { a.onended = () => { setSpeaking(false); URL.revokeObjectURL(url); res(); }; a.onerror = () => { setSpeaking(false); res(); }; });
          await a.play();
          await ended;
        } catch {
          if (canSpeak) await new Promise<void>((res) => { const u = new SpeechSynthesisUtterance(t); u.lang = 'pt-BR'; u.onend = () => res(); u.onerror = () => res(); window.speechSynthesis.speak(u); });
        }
      })();
    }
    if (!canSpeak) return Promise.resolve();
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = 'pt-BR';
    const v = window.speechSynthesis.getVoices().find((x) => x.lang.toLowerCase().startsWith('pt-br')) ?? window.speechSynthesis.getVoices().find((x) => x.lang.toLowerCase().startsWith('pt'));
    if (v) u.voice = v;
    u.rate = 1.02;
    u.onstart = () => setSpeaking(true);
    return new Promise<void>((res) => { u.onend = () => { setSpeaking(false); res(); }; u.onerror = () => { setSpeaking(false); res(); }; window.speechSynthesis.speak(u); });
  }, [voice, canSpeak, q.data?.serverSpeaks, token]);

  const [liveStarted, setLiveStarted] = useState(false);
  const [liveActive, setLiveActive] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [fallback, setFallback] = useState<string | null>(null);
  /** a voz caiu: segue por texto, avisa na tela e em voz (do navegador, que não depende de provedor), e abre a conversa por texto se ela ainda não começou */
  const onVoiceTrouble = (detail: string) => {
    setMode('turns'); setLiveActive(false); setLiveStarted(false);
    const msg = 'estou com um problema na voz neste momento. vamos continuar por texto: pode escrever ou tocar no microfone para ditar.';
    setFallback(`${msg} (${detail})`);
    if (canSpeak) { try { window.speechSynthesis.cancel(); const u = new SpeechSynthesisUtterance(msg); u.lang = 'pt-BR'; window.speechSynthesis.speak(u); } catch { /* sem voz do navegador */ } }
    if (!turns.some((t) => t.role === 'assistant')) start.mutate('turns');
  };
  const start = useMutation({
    mutationFn: (m: 'turns' | 'live') => pub<Peek>(`/conversa/${token}/start`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name: intro.name, role: intro.role || undefined, relation: intro.relation || undefined, mode: m }) }),
    onSuccess: (d, m) => {
      setTurns(d.turns);
      if (m === 'live') { setMode('live'); setLiveStarted(true); return; } // a voz contínua abre a conversa; nada de segunda apresentação
      const last = d.turns[d.turns.length - 1]; if (last?.role === 'assistant') void speak(last.text);
    },
  });
  const send = useMutation({
    mutationFn: async (payload: { text: string; files: File[]; audio?: File }) => {
      lastSent.current = payload;
      const form = new FormData();
      if (payload.text) form.append('text', payload.text);
      // a fala vai como áudio: quem transcreve é o servidor, com o glossário do cliente (o reconhecimento do aparelho erra e entra em laço)
      if (payload.audio) form.append('audio', payload.audio);
      for (const f of payload.files.slice(0, 6)) form.append('attachments', f);
      return pub<{ heard: string; reply: string; done: boolean; degraded?: boolean; attachments: { fileId: string; name: string; mimeType: string }[] }>(`/conversa/${token}/turn`, { method: 'POST', body: form });
    },
    onMutate: (payload) => { setTurns((t) => { const last = t[t.length - 1]; return last?.role === 'user' && last.text === payload.text ? t : [...t, { id: `tmp-${Date.now()}`, index: t.length, role: 'user', text: payload.text || (payload.audio ? 'ouvindo o que você disse…' : `(enviando ${payload.files.length} arquivo(s))`), attachments: payload.files.map((f) => ({ fileId: '', name: f.name, mimeType: f.type })) }]; }); setText(''); setFiles([]); },
    onSuccess: (r) => {
      // a fala gravada aparece como o servidor a entendeu
      setTurns((t) => { const last = t[t.length - 1]; const fixed = last?.role === 'user' && last.id.startsWith('tmp-') && r.heard ? [...t.slice(0, -1), { ...last, text: r.heard }] : t; return [...fixed, { id: `a-${Date.now()}`, index: fixed.length, role: 'assistant', text: r.reply, attachments: [] }]; });
      setDone(r.done);
      if (r.degraded && !fallback) setFallback('a Machine está com um problema técnico e segue com perguntas mais simples. o que você disser continua sendo guardado. (modelo de texto indisponível)');
      // a Machine se despediu: deixa a fala terminar e encerra sozinha (a pessoa não precisa achar botão no celular)
      void speak(r.reply).then(() => { if (r.done) finish.mutate(); });
    },
  });
  const finish = useMutation({ mutationFn: () => pub<{ ok: boolean }>(`/conversa/${token}/finish`, { method: 'POST' }), onSuccess: () => { setFinished(true); window.speechSynthesis?.cancel(); audioRef.current?.pause(); if (recRef.current) { recRef.current.send = false; if (recRef.current.recorder.state !== 'inactive') recRef.current.recorder.stop(); } } });

  // saiu da página no meio da gravação: solta o microfone sem enviar
  useEffect(() => () => { const r = recRef.current; if (r) { r.send = false; window.clearInterval(r.timer); r.stream.getTracks().forEach((t) => t.stop()); } }, []);
  /** Solta o microfone e o cronômetro. */
  const releaseMic = () => {
    const r = recRef.current;
    if (!r) return;
    window.clearInterval(r.timer);
    r.stream.getTracks().forEach((t) => t.stop());
    void r.ctx?.close().catch(() => undefined);
    recRef.current = null;
    setListening(false);
    setSeconds(0);
  };
  /** Grava a fala no aparelho. Nada é transcrito aqui: ao parar, o áudio vai para a Machine. */
  const listen = async () => {
    if (recRef.current || send.isPending) return;
    unlockAudio();
    setMicError(null);
    window.speechSynthesis?.cancel();
    audioRef.current?.pause();
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch {
      setMicError('não consegui usar o microfone. libere o acesso no navegador, ou escreva a resposta.');
      return;
    }
    const mime = recordingMime() ?? '';
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const state: Recording = { recorder, stream, chunks: [] as Blob[], timer: 0, startedAt: Date.now(), send: true };
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      ctx.createMediaStreamSource(stream).connect(analyser);
      state.ctx = ctx; state.analyser = analyser;
    } catch { /* sem onda: a gravação segue igual */ }
    recorder.ondataavailable = (e) => { if (e.data.size > 0) state.chunks.push(e.data); };
    recorder.onstop = () => {
      const took = (Date.now() - state.startedAt) / 1000;
      const type = recorder.mimeType || mime || 'audio/webm';
      const blob = new Blob(state.chunks, { type });
      const wanted = state.send;
      releaseMic();
      if (!wanted) return;
      if (took < 0.8 || blob.size < 1200) { setMicError('a gravação ficou curta demais. segure um pouco mais e fale.'); return; }
      const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
      send.mutate({ text: text.trim(), files, audio: new File([blob], `fala.${ext}`, { type: type.split(';')[0] }) });
    };
    state.timer = window.setInterval(() => {
      const s = Math.floor((Date.now() - state.startedAt) / 1000);
      setSeconds(s);
      if (s >= MAX_RECORDING_S) recorder.stop();
    }, 500);
    recRef.current = state;
    setListening(true);
    recorder.start(1000);
  };
  /** Para e envia (padrão) ou para e joga fora. */
  const stopListening = (sendIt = true) => {
    const r = recRef.current;
    if (!r) return;
    r.send = sendIt;
    if (r.recorder.state !== 'inactive') r.recorder.stop(); else releaseMic();
  };
  const submit = () => { unlockAudio(); if (listening) { stopListening(true); return; } if (!text.trim() && files.length === 0) return; send.mutate({ text: text.trim(), files }); };

  if (q.isPending) return <div className="rr-conversa"><p className="rr-faint">abrindo…</p></div>;
  if (q.isError || !q.data) return <div className="rr-conversa"><div className="rr-conversa__card"><RaroBrand /><Notice tone="error">{q.error ? (q.error as Error).message : 'link inválido'}</Notice></div></div>;
  const d = q.data;
  const started = turns.length > 0 || liveStarted;

  return (
    <div className="rr-conversa">
      <div className={cx('rr-conversa__card', !started && !finished && 'rr-conversa__card--open')}>
        <header className="rr-conversa__head">
          <RaroBrand />
          <div className="rr-stack rr-stack--1" style={{ textAlign: 'right' }}>
            <span className="rr-label">{d.organization}{d.project ? ` · ${d.project}` : ''}</span>
            <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{d.kind === 'briefing' ? 'conversa de briefing' : 'conversa com a Machine'}</span>
          </div>
        </header>

        {finished ? (
          <div className="rr-conversa__end">
            <span className="rr-conversa__end-dot" aria-hidden />
            <p className="rr-display" style={{ fontSize: 'var(--rr-text-2xl)' }}><Serif italic>obrigada{d.participant.name ? `, ${d.participant.name.split(' ')[0]}` : ''}.</Serif></p>
            <p className="rr-muted">o que você contou já está com a Raro. o áudio foi encerrado e nada mais é gravado.</p>
            <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{turns.filter((t) => t.role === 'user').length} resposta(s){turns.reduce((n, t) => n + t.attachments.length, 0) > 0 ? ` · ${turns.reduce((n, t) => n + t.attachments.length, 0)} anexo(s)` : ''} · {d.organization}</p>
            <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>pode fechar esta página.</p>
          </div>
        ) : !started ? (
          <div className="rr-stack rr-stack--5">
            <div className="rr-stack rr-stack--2">
              <span className="rr-label rr-label--accent">{d.organization}{d.invitedBy ? ` · a convite de ${d.invitedBy}, da Raro` : ' · a convite da Raro'}{d.referredBy ? ` · por indicação de ${d.referredBy}` : ''}</span>
              <h1 className="rr-display" style={{ fontSize: 'var(--rr-text-2xl)' }}><Serif italic>{d.intro?.headline ?? `uma conversa sobre a ${d.organization}`}</Serif></h1>
              <p className="rr-muted">{d.intro?.body ?? 'a Raro está trabalhando com a empresa e quer ouvir você. a Machine pergunta e você responde falando ou escrevendo, do seu jeito. não tem resposta certa.'}</p>
              {(d.intro?.topics.length ?? 0) > 0 && (
                <div className="rr-stack rr-stack--2">
                  <span className="rr-label">vamos passar por</span>
                  <div className="rr-row rr-row--wrap" style={{ gap: 6 }}>{d.intro!.topics.map((t) => <span key={t} className="rr-chip">{t}</span>)}</div>
                </div>
              )}
              <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>leva uns {d.intro?.minutes ?? 10} minutos e dá para parar quando quiser. pode mandar foto, vídeo ou documento no meio. o que você disser fica com a Raro e não vai para terceiros.</p>
              {!canListen && <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>seu navegador não grava áudio; dá para escrever normalmente.</p>}
            </div>
            {d.participant.name ? (
              <p className="rr-heading">oi, {d.participant.name}.{d.participant.role ? ` ${d.participant.role}` : ''}{d.participant.relation ? ` · ${d.participant.relation}` : ''} <button type="button" className="rr-budget__toggle" style={{ fontSize: 'var(--rr-text-xs)' }} onClick={() => { setIntro({ name: '', role: '', relation: '' }); }}>não é você?</button></p>
            ) : (
              <>
                <Field label="Como você se chama?" htmlFor="cv-n"><Input id="cv-n" value={intro.name} onChange={(e) => setIntro({ ...intro, name: e.target.value })} autoFocus /></Field>
                <div className="rr-row" style={{ gap: 'var(--rr-space-3)', alignItems: 'flex-start' }}>
                  <Field label={d.kind === 'briefing' ? 'O que você faz na empresa' : 'O que você faz'} htmlFor="cv-r"><Input id="cv-r" value={intro.role} onChange={(e) => setIntro({ ...intro, role: e.target.value })} placeholder={d.kind === 'briefing' ? 'diretora comercial, sócio, gerente…' : 'decoradora, lojista, vendedora…'} /></Field>
                  <Field label={d.kind === 'briefing' ? 'Há quanto tempo está nela' : 'Sua relação com a empresa'} htmlFor="cv-l"><Input id="cv-l" value={intro.relation} onChange={(e) => setIntro({ ...intro, relation: e.target.value })} placeholder={d.kind === 'briefing' ? 'fundadora, 12 anos…' : 'cliente há 6 anos, fornecedor…'} /></Field>
                </div>
              </>
            )}
            {start.isError && <Notice tone="error">{(start.error as Error).message}</Notice>}
            <div className="rr-stack rr-stack--2">
              {d.realtimeAvailable ? (
                <>
                  <span className="rr-label">como você prefere conversar?</span>
                  <div className="rr-modos">
                    <button type="button" className="rr-modo rr-modo--main" disabled={(!d.participant.name && intro.name.trim().length < 1) || start.isPending} onClick={() => start.mutate('live')}>
                      <Icon name="phone" size={20} />
                      <span className="rr-modo__title">{start.isPending ? 'chamando a Machine…' : 'por ligação'}</span>
                      <span className="rr-modo__text">a Machine fala e escuta ao vivo, como num telefonema. você não aperta nada, só conversa.</span>
                    </button>
                    <button type="button" className="rr-modo" disabled={(!d.participant.name && intro.name.trim().length < 1) || start.isPending} onClick={() => { unlockAudio(); start.mutate('turns'); }}>
                      <Icon name="comment" size={20} />
                      <span className="rr-modo__title">por mensagem</span>
                      <span className="rr-modo__text">como no WhatsApp: a Machine pergunta, você grava um áudio ou escreve, e envia quando quiser.</span>
                    </button>
                  </div>
                  <p className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>os dois precisam de microfone para falar. dá para trocar no meio da conversa.</p>
                </>
              ) : (
                <div className="rr-row"><Button variant="primary" disabled={(!d.participant.name && intro.name.trim().length < 1) || start.isPending} onClick={() => { unlockAudio(); start.mutate('turns'); }}>{start.isPending ? 'chamando a Machine…' : 'começar a conversa'}</Button></div>
              )}
            </div>
          </div>
        ) : (
          <>
            {liveActive && !showTranscript ? null : (
            <div className="rr-conversa__turns">
              {turns.map((t) => (
                <div key={t.id} className={cx('rr-turn', `rr-turn--${t.role}`)}>
                  {t.role === 'assistant' ? <span className="rr-turn__who rr-turn__who--machine">m.</span> : <Avatar name={d.participant.name ?? intro.name ?? '?'} accent />}
                  <div className="rr-turn__bubble">
                    <p>{t.text}</p>
                    {t.attachments.length > 0 && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{t.attachments.map((a) => a.name).join(' · ')}</span>}
                  </div>
                </div>
              ))}
              {send.isPending && <div className="rr-turn rr-turn--assistant"><span className="rr-turn__who rr-turn__who--machine">m.</span><div className="rr-turn__bubble rr-turn__bubble--thinking">…</div></div>}
              <div ref={endRef} />
            </div>
            )}
            {fallback && <Notice tone="error"><span className="rr-stack rr-stack--1"><span>{fallback.split(' (')[0]}</span><span className="rr-faint" style={{ fontSize: 'var(--rr-text-2xs)' }}>{fallback.includes(' (') ? fallback.slice(fallback.indexOf(' (') + 2, -1) : ''}</span></span></Notice>}
            {send.isError && <Notice tone="error">não consegui falar com a Machine agora ({(send.error as Error).message}). sua resposta não se perdeu: <button type="button" className="rr-budget__toggle" onClick={() => { const again = lastSent.current; if (again) send.mutate(again); else { const last = [...turns].reverse().find((t) => t.role === 'user'); if (last) send.mutate({ text: last.text, files: [] }); } }}>enviar de novo</button></Notice>}
            {micError && <Notice tone="error">{micError}</Notice>}
            {mode === 'live' && d.realtimeAvailable && (
              <RealtimeVoice token={token} autoStart={liveStarted} onActive={setLiveActive} onTrouble={onVoiceTrouble} caption={[...turns].reverse().find((t) => t.role === 'assistant')?.text ?? null} onTurn={(role, t) => setTurns((prev) => [...prev, { id: `rt-${Date.now()}-${Math.random()}`, index: prev.length, role, text: t, attachments: [] }])} onEnd={() => setMode('turns')} onFinish={() => { setDone(true); finish.mutate(); }} />
            )}
            {finish.isError && <Notice tone="error">não consegui encerrar: {(finish.error as Error).message}. <button type="button" className="rr-budget__toggle" onClick={() => finish.mutate()}>tentar de novo</button></Notice>}
            {liveActive && <button type="button" className="rr-conversa__transcript-toggle" onClick={() => setShowTranscript((v) => !v)}>{showTranscript ? 'esconder transcrição' : 'ver transcrição'}</button>}
            {liveActive || mode === 'live' ? null : (
            <div className="rr-composer">
              {files.length > 0 && <div className="rr-row rr-row--wrap" style={{ gap: 6 }}>{files.map((f, i) => <button key={`${f.name}-${i}`} type="button" className="rr-chip" onClick={() => setFiles(files.filter((_, j) => j !== i))} title="tirar este arquivo">{f.name} <Icon name="x" size={10} /></button>)}</div>}
              {listening ? (
                <div className="rr-composer__row rr-composer__row--rec">
                  <button type="button" className="rr-composer__icon" onClick={() => stopListening(false)} title="descartar a gravação" aria-label="descartar a gravação"><Icon name="x" size={18} /></button>
                  <VoiceWave state="user" getLevels={micLevels} bars={36} height={40} className="rr-composer__wave" />
                  <span className="rr-composer__time">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</span>
                  <button type="button" className="rr-composer__send" onClick={() => stopListening(true)} title="enviar o áudio" aria-label="enviar o áudio"><Icon name="send" size={18} /></button>
                </div>
              ) : (
                <div className="rr-composer__row">
                  <label className="rr-composer__icon" title="mandar foto, vídeo ou documento" aria-label="mandar foto, vídeo ou documento"><Icon name="paperclip" size={18} /><input type="file" accept="image/*,video/*,audio/*,.pdf" multiple hidden onChange={(e) => setFiles([...files, ...Array.from(e.target.files ?? [])].slice(0, 6))} /></label>
                  <textarea className="rr-composer__text" rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder={send.isPending ? 'a Machine está ouvindo…' : 'mensagem'} disabled={send.isPending} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }} />
                  {text.trim() || files.length > 0 || !canListen
                    ? <button type="button" className="rr-composer__send" onClick={submit} disabled={send.isPending || (!text.trim() && files.length === 0)} title="enviar" aria-label="enviar"><Icon name="send" size={18} /></button>
                    : <button type="button" className="rr-composer__send" onClick={() => void listen()} disabled={send.isPending} title="gravar um áudio" aria-label="gravar um áudio"><Icon name="mic" size={18} /></button>}
                </div>
              )}
              <div className="rr-composer__foot">
                <span>{listening ? 'gravando. toque na seta para enviar.' : `${turns.filter((t) => t.role === 'user').length} de ${d.maxTurns}`}</span>
                <span className="rr-row" style={{ gap: 14 }}>
                  {canSpeak && <button type="button" className="rr-composer__link" onClick={() => { setVoice(!voice); if (voice) { window.speechSynthesis.cancel(); audioRef.current?.pause(); } }} title={voice ? 'a Machine lê as perguntas em voz alta' : 'a Machine não lê as perguntas'}><Icon name={voice ? 'volume' : 'volume-off'} size={14} className={speaking ? 'rr-icon--pulse' : undefined} /> {voice ? 'com voz' : 'sem voz'}</button>}
                  {d.realtimeAvailable && <button type="button" className="rr-composer__link" onClick={() => { window.speechSynthesis?.cancel(); audioRef.current?.pause(); stopListening(false); setMode('live'); }}><Icon name="phone" size={14} /> por ligação</button>}
                  {(done || turns.filter((t) => t.role === 'user').length >= 2) && <button type="button" className={cx('rr-composer__link', done && 'rr-composer__link--strong')} onClick={() => finish.mutate()} disabled={finish.isPending}>{done ? 'encerrar e enviar' : 'encerrar'}</button>}
                </span>
              </div>
            </div>
            )}
            {mode === 'live' && !liveActive && d.realtimeAvailable && <button type="button" className="rr-composer__link" onClick={() => setMode('turns')}><Icon name="comment" size={14} /> voltar para mensagem</button>}
          </>
        )}
      </div>
    </div>
  );
}
